import React, { useEffect, useRef, useState } from "react";
import {
  Row,
  Col,
  Image,
  Form,
  Button,
  Modal,
  OverlayTrigger,
  Tooltip,
  Popover,
} from "react-bootstrap";
import { Link, useNavigate, useParams, useBlocker } from "react-router-dom";
import axios from "axios";
import { Formik, ErrorMessage, Field } from "formik";
import * as Yup from "yup";
import { jwtDecode } from "jwt-decode";
import Card from "../../../components/Card";
import url from "../../../configs/config";
import InputField from "../../../components/input/input-field";
import avatars1 from "../../../assets/images/avatars/01.png";
import CustomPhoneInput from "../../../components/input/phone-input";
import AlertNotificationSimple from "../../../components/alert/alert-notification-simple";
import FocusError from "../../../components/error/error-focus";
import "react-phone-input-2/lib/style.css";
import FadeLoaderComponent from "../../../components/loader-component";
import { roleTypes } from "../../../data/roleType";

const rolePermissions = {
  [roleTypes.ADMIN]: [
    "receiving",
    "purchase_order",
    "product_request",
    "job",
    "rack_management",
    "system",
    "phase",
    "vendor",
    "manufacturer",
    "general_product_category",
    "general_product",
    "warehouse",
    "location",
    "stakeholder",
    "user",
  ],
  [roleTypes.SUPPLIER]: ["product_request", "purchase_order", "receiving"],
  [roleTypes.PURCHASING]: ["product_request", "purchase_order", "receiving"],
  [roleTypes.WAREHOUSE]: [
    "product_request",
    "rack_management",
    "warehouse",
    "system",
    "phase",
    "user",
    "receiving",
    "job",
    "location",
    "vendor",
    "stakeholder",
  ],
  [roleTypes.TECHNICIAN]: [
    "product_request",
    "rack_management",
    "warehouse",
    "system",
    "phase",
    "receiving",
    "job",
    "location",
    "vendor",
    "stakeholder",
  ],
};

const getFilteredPermissions = (allPermissions, userRoles, rolePermissions) => {
  const allowedEntities = new Set();

  userRoles.forEach((role) => {
    const roleType = role.role_type;
    if (rolePermissions[roleType]) {
      rolePermissions[roleType].forEach((entitySlug) =>
        allowedEntities.add(entitySlug),
      );
    }
  });

  return allPermissions.filter((permission) =>
    allowedEntities.has(permission.slug),
  );
};

const FormObserver = ({ dirty, setHasUnsavedChanges }) => {
  useEffect(() => {
    setHasUnsavedChanges(dirty);
  }, [dirty, setHasUnsavedChanges]);
  return null;
};

const WarehouseAccountEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const alertRef = useRef(null);
  const alertPermissionRef = useRef();
  const [loader, setLoader] = useState(true);
  const [errors, setErrors] = useState({});
  const [roles, setRoles] = useState([]);
  const [userWarehousesRolesPermissions, setUserWarehousesRolesPermissions] =
    useState();
  const [user, setUser] = useState();
  const [userProfilePicture, setUserProfilePicture] = useState();
  const [permissions, setPermissions] = useState([]);
  const token = localStorage.getItem("coyote-token");
  const [decodedToken, setDecodedToken] = useState();

  const [alert, setAlert] = useState({
    message: "",
    type: "",
    show: false,
    alertFor: "",
  });

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const allowNavigationRef = useRef(false);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (allowNavigationRef.current) return false;
    return (
      hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname
    );
  });

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (blocker.state === "blocked") {
      if (hasUnsavedChanges && !allowNavigationRef.current) {
        setShowDiscardModal(true);
        setPendingNavigation(() => blocker.proceed);
      } else {
        blocker.reset();
        setShowDiscardModal(false);
        setPendingNavigation(null);
      }
    }
  }, [blocker.state, hasUnsavedChanges, blocker]);

  useEffect(() => {
    return () => {
      if (blocker.state === "blocked" && !allowNavigationRef.current)
        blocker.reset();
      allowNavigationRef.current = false;
    };
  }, [blocker]);

  const handleDiscardChanges = () => {
    if (blocker.state === "blocked") {
      allowNavigationRef.current = true;
      setShowDiscardModal(false);
      setPendingNavigation(null);
      if (pendingNavigation) {
        pendingNavigation();
      } else if (blocker.proceed) {
        blocker.proceed();
      }
    }
  };

  const handleCancelDiscard = () => {
    allowNavigationRef.current = false;
    if (blocker.state === "blocked") blocker.reset();
    setShowDiscardModal(false);
    setPendingNavigation(null);

    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    if (token && typeof token === "string") {
      try {
        const decoded = jwtDecode(token);
        setDecodedToken(decoded?.user);
      } catch (error) {
        console.error("Failed to decode token", error);
      }
    }
  }, [token]);

  const getPermissions = async () => {
    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };
      const response = await axios.get(
        `${url.backendURL}permission/active-user-permissions`,
        config,
      );
      return response.data.response.data;
    } catch (err) {
      handleErrorPermission(err, "permission-edit");
    }
  };

  const getRoles = async () => {
    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };
      const response = await axios.get(
        `${url.backendURL}warehouse/role/all`,
        config,
      );
      setRoles(response.data.response.data);
      return response.data.response.data;
    } catch (err) {
      handleError(err, "user-edit");
    }
  };

  const getUserData = async () => {
    if (
      decodedToken?.roles.some((role) =>
        [roleTypes.WAREHOUSE].includes(role.role_type),
      )
    ) {
      try {
        const config = {
          headers: {
            Authorization: `${token}`,
          },
        };
        const response = await axios.get(
          `${url.backendURL}warehouse/user/${id}`,
          config,
        );

        const userData = response.data.response.data;
        const { profilePicture, warehouseRoles, ...otherData } = userData;

        const allPermissions = await getPermissions();

        const filteredPermissions = getFilteredPermissions(
          allPermissions,
          decodedToken?.roles || [],
          rolePermissions,
        );

        const initialValues = {};

        filteredPermissions?.forEach((permission) => {
          const entitySlug = permission.slug;
          initialValues[entitySlug] = {};
          const validActions = permission.permissions.map(
            (p) => p.permission_slug,
          );

          validActions.forEach((action) => {
            initialValues[entitySlug][action] = false;
          });
        });

        await getRoles();

        const userRolesWarehousesPermissions = warehouseRoles
          ? {
              warehouseId: warehouseRoles.warehouseId || null,
              isWarehouseHead: warehouseRoles.isWarehouseHead || 0,
              roles: Array.isArray(warehouseRoles.roles)
                ? warehouseRoles.roles
                : [],
              permissions: {
                ...initialValues,
                ...warehouseRoles.permissions,
              },
            }
          : {
              warehouseId: null,
              isWarehouseHead: 0,
              roles: [],
              permissions: { ...initialValues },
            };

        setPermissions(filteredPermissions);
        setLoader(false);
        setUserProfilePicture(profilePicture);
        setUserWarehousesRolesPermissions(userRolesWarehousesPermissions || {});
        setUser({ ...otherData, userRolesWarehousesPermissions });
      } catch (err) {
        handleError(err, "user-edit");
        setLoader(false);
      }
    }
  };

  useEffect(() => {
    if (
      decodedToken?.roles.some((role) =>
        [roleTypes.WAREHOUSE].includes(role.role_type),
      )
    ) {
      getUserData();
    }
  }, [decodedToken, token]);

  const handleError = (err, alertFor) => {
    const { response } = err;
    if (alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    if (
      response?.data?.error === "jwt expired" ||
      response?.data?.error === "Unauthorized. User not found"
    ) {
      localStorage.removeItem("coyote-token");
      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN,
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "expired" },
        },
      );
    } else {
      const responseErrors = response?.data?.error || {};
      setErrors(responseErrors);
      setAlert({
        message: response ? response.data.message : "An error occurred",
        type: "danger",
        show: true,
        alertFor,
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    }
  };

  const handleErrorPermission = (err, alertFor) => {
    const { response } = err;

    if (alertPermissionRef.current) {
      alertPermissionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
    if (
      response?.data?.error === "jwt expired" ||
      response?.data?.error === "Unauthorized. User not found"
    ) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN,
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "expired" },
        },
      );
    } else {
      const responseErrors = response?.data?.error || {};
      setErrors(responseErrors);
      setAlert({
        message: response ? response.data.message : "An error occurred",
        type: "danger",
        show: true,
        alertFor,
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    }
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    if (
      decodedToken?.roles.some((role) =>
        [roleTypes.WAREHOUSE].includes(role.role_type),
      )
    ) {
      setSubmitting(true);
      delete values.id;
      delete values.isWarehouseHead;
      delete values.warehouseId;

      const data = new FormData();
      Object.keys(values).forEach((key) => {
        if (
          key !== "profilePicture" &&
          key !== "roles" &&
          key !== "permissions"
        ) {
          data.append(key, values[key]);
        }
      });
      const fileInput = document.getElementById("profilePicture");
      if (values.profilePicture) {
        const file = fileInput.files[0];
        data.append("profilePicture", file);
      }
      if (values.roles && values.permissions) {
        const warehousesData = [
          {
            warehouseId: values.warehouseId || decodedToken?.warehouse_id || "",
            roles: values.roles,
            permissions: values.permissions,
            isWarehouseHead: values.isWarehouseHead || 0,
            generalRole: false,
          },
        ];
        data.append("warehouses", JSON.stringify(warehousesData));
      }

      try {
        const config = {
          headers: {
            Authorization: `${token}`,
            "Content-Type": "multipart/form-data",
          },
        };

        const response = await axios.put(
          `${url.backendURL}warehouse/user`,
          data,
          config,
        );

        setHasUnsavedChanges(false);
        allowNavigationRef.current = true;
        if (blocker.state === "blocked") blocker.reset();

        if (alertRef.current) {
          alertRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
        setErrors({});
        setAlert({
          message: response.data.message || "User updated successfully",
          type: "success",
          show: true,
          alertFor: "user-edit",
        });

        await getUserData();
        allowNavigationRef.current = false;

        setTimeout(() => {
          setAlert({ show: false, alertFor: "", message: "", type: "" });
        }, 3000);
      } catch (err) {
        handleError(err, "user-edit");
      } finally {
        setSubmitting(false);
      }
    }
  };

  const validationSchema = () =>
    Yup.object().shape({
      profilePicture: Yup.mixed()
        .test(
          "fileType",
          "Image must be a valid file (jpeg, png, jpg)",
          (value) =>
            !value ||
            (value &&
              ["image/jpeg", "image/png", "image/jpg"].includes(value.type)),
        )
        .nullable(),

      name: Yup.string()
        .min(1, "Name must be at least 1 character long")
        .max(255, "Name must not exceed 255 characters")
        .trim()
        .matches(
          /^(?=.*[A-Za-z])[A-Za-z0-9\s!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+$/,
          "Name must start with a letter and can include numbers, spaces, but cannot be entirely numbers",
        )
        .required("Name is required"),
      email: Yup.string()
        .trim()
        .required("Email is required")
        .email("Enter a valid email address")
        .test(
          "no-spaces",
          "Email may not contain any spaces at the beginning or end",
          (value) => !value || value.trim() === value,
        )
        .test("min-domain-segments", "Enter a valid email address", (value) => {
          if (!value) return true;
          const parts = value.split("@");
          if (parts.length !== 2) return false;
          const domainSegments = parts[1].split(".");
          return (
            domainSegments.length >= 2 &&
            domainSegments.every((s) => s.length >= 2)
          );
        })
        .test("realistic-email", "Enter a valid email address", (value) => {
          if (!value) return true;
          const [local, domain] = value.split("@");
          if (!local || !domain) return false;

          if (/^\d+$/.test(local)) return false;
          if (local.length > 64) return false;

          if (!/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(domain)) return false;

          return true;
        }),
      phoneNumber: Yup.string()
        .trim()
        .matches(/^[0-9+\-]{10,15}$/, "Phone number must be 10 to 15 digits")
        .optional()
        .nullable()
        .test(
          "trim",
          "Phone number may not contain any spaces at the beginning or end",
          (value) => value === (value && value.trim()),
        ),
    });

  const validationWarehouseSchema = () =>
    Yup.object().shape({
      warehouseId: Yup.number()
        .integer("Warehouse is invalid")
        .positive("Warehouse is required")
        .when("supplier", {
          is: (value) => value === false || value === null,
          then: () => Yup.number().required("Warehouse is required"),
          otherwise: (schema) => Yup.number().notRequired(),
        }),
      isWarehouseHead: Yup.number()
        .integer("Warehouse head is main is invalid")
        .when("supplier", {
          is: (value) => value === false || value === null,
          then: () =>
            Yup.number().required("Warehouse head is main is required"),
          otherwise: (schema) => Yup.number().notRequired(),
        }),
      roles: Yup.array()
        .min(1, "At least one role is required")
        .when("supplier", {
          is: (value) => value === true,
          then: () => Yup.array().max(1, "Only one role is allowed"),
          otherwise: (schema) => Yup.array().min(1, "Roles are required"),
        })
        .required("Roles are required"),
      permissions: Yup.object()
        .shape(
          permissions.reduce((acc, permission) => {
            const entitySlug = permission.slug;
            const filteredActions = permission.permissions.map(
              (p) => p.permission_slug,
            );

            if (filteredActions.length > 0) {
              acc[entitySlug] = Yup.object(
                filteredActions.reduce(
                  (obj, action) => ({
                    ...obj,
                    [action]: Yup.boolean().required(
                      `${action} is required for ${permission.label}`,
                    ),
                  }),
                  {},
                ),
              );
            }

            return acc;
          }, {}),
        )
        .test(
          "at-least-one-action-true",
          "At least one permission must be selected",
          (value) => {
            if (!value) {
              return false;
            }
            const isValid = Object.values(value).some((entityPermissions) => {
              return Object.values(entityPermissions).some(
                (action) => action === true,
              );
            });
            return isValid;
          },
        ),
    });

  const [options, setOptions] = useState([]);

  useEffect(() => {
    const roleOptions =
      roles.length > 0 &&
      roles.map((role) => ({
        value: role.id,
        label: role.name,
        roleType: role.role_type || role.roleType,
      }));
    setOptions(roleOptions);
  }, [roles]);

  const scrollToFirstError = (errors, touched) => {
    const firstErrorKey = Object.keys(errors).find((key) => {
      if (typeof errors[key] === "object" && errors[key] !== null) {
        return Object.keys(errors[key]).some(
          (nestedKey) => touched[key] && touched[key][nestedKey],
        );
      }
      return touched[key];
    });

    if (firstErrorKey) {
      let targetElement =
        document.querySelector(`[name="${firstErrorKey}"]`) ||
        document.querySelector(`#${firstErrorKey}`) ||
        document.querySelector(`[name*="${firstErrorKey}"]`);

      if (!targetElement && typeof errors[firstErrorKey] === "object") {
        const nestedErrorKey = Object.keys(errors[firstErrorKey])[0];
        targetElement =
          document.querySelector(
            `[name="${firstErrorKey}.${nestedErrorKey}"]`,
          ) || document.querySelector(`[name*="${firstErrorKey}"]`);
      }

      if (!targetElement) {
        const errorElement = document.querySelector(".text-danger");
        if (errorElement) {
          targetElement =
            errorElement.closest(".form-group, .mb-3, .col") || errorElement;
        }
      }

      if (targetElement?.type === "hidden") {
        const fallbackElement =
          document.getElementById(firstErrorKey) ||
          document.querySelector(`#${firstErrorKey}-anchor`) ||
          document.querySelector(".text-danger");
        if (fallbackElement) targetElement = fallbackElement;
      }

      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });

        if (targetElement.focus && typeof targetElement.focus === "function") {
          setTimeout(() => targetElement.focus(), 300);
        }
      }
    }
  };

  const [isSubmittingButton, setIsSubmittingButton] = useState(false);

  return (
    <>
      <Modal
        show={showDiscardModal}
        onHide={handleCancelDiscard}
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Header className="custom-modal-header d-flex justify-content-center items-center relative">
          <Modal.Title>Unsaved Changes</Modal.Title>
          <button
            type="button"
            className="btn-close-svg"
            style={{ position: "absolute", right: "1rem" }}
            onClick={handleCancelDiscard}
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </Modal.Header>
        <Modal.Body className="text-center px-4 pt-2">
          <p
            className="text-muted mb-4 px-2"
            style={{ fontSize: "1rem", lineHeight: "1.5" }}
          >
            You have unsaved changes. Do you want to discard these? Click update
            to save the changes.
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-end pb-4 pt-0">
          <Button
            type="button"
            className="btn btn-white me-4"
            variant="btn btn-white"
            onClick={handleDiscardChanges}
            style={{
              borderRadius: "25px",
              fontWeight: "500",
              minWidth: "100px",
              borderColor: "#6c757d",
              color: "#6c757d",
            }}
          >
            Discard
          </Button>
          <Button
            className="px-4 py-2"
            style={{
              backgroundColor: "#3a57e8",
              borderColor: "#3a57e8",
              borderRadius: "25px",
              fontWeight: "500",
              minWidth: "100px",
            }}
            onClick={handleCancelDiscard}
          >
            Continue
          </Button>
        </Modal.Footer>
      </Modal>

      <div>
        <Formik
          enableReinitialize={true}
          initialValues={{
            phoneNumber: user?.phoneNumber || "",
            email: user?.email,
            name: user?.name,
            profilePicture: user?.profilePicture || null,
            ...userWarehousesRolesPermissions,
            id: user?.id,
          }}
          validationSchema={() =>
            validationSchema().concat(validationWarehouseSchema())
          }
          onSubmit={handleSubmit}
        >
          {({
            values,
            dirty,
            handleChange,
            handleBlur,
            setFieldValue,
            handleSubmit: formikHandleSubmit,
            touched,
            isSubmitting,
            setFieldError,
            setFieldTouched,
            errors: errorsformik,
          }) => {
            useEffect(() => {
              if (Object.keys(errorsformik).length > 0) {
                setTimeout(() => {
                  scrollToFirstError(errorsformik, touched);
                }, 100);
              }
              setIsSubmittingButton(false);
            }, [errorsformik, touched]);

            return (
              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsSubmittingButton(true);
                  formikHandleSubmit();
                }}
              >
                <FormObserver
                  dirty={dirty}
                  setHasUnsavedChanges={setHasUnsavedChanges}
                />
                <FocusError />
                <Row>
                  <Col xl="3" lg="4" className="d-flex">
                    <div style={{ width: "100%", display: "grid" }}>
                      <Card>
                        <Card.Header className="d-flex justify-content-between">
                          <div className="header-title">
                            <h4 className="card-title">Edit User</h4>
                          </div>
                        </Card.Header>

                        <Card.Body>
                          {user && !loader && (
                            <>
                              <Form.Group className="form-group">
                                <div className="profile-img-edit position-relative">
                                  <Image
                                    className="theme-color-default-img profile-pic rounded avatar-100"
                                    src={
                                      values?.profilePicture
                                        ? URL.createObjectURL(
                                            values.profilePicture,
                                          )
                                        : userProfilePicture
                                          ? userProfilePicture
                                          : avatars1
                                    }
                                    alt="profile-pic"
                                  />

                                  <div
                                    className="upload-icone bg-primary"
                                    onClick={() =>
                                      document
                                        .getElementById("profilePicture")
                                        .click()
                                    }
                                  >
                                    <svg
                                      className="upload-button"
                                      width="14"
                                      height="14"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        fill="#ffffff"
                                        d="M14.06,9L15,9.94L5.92,19H5V18.08L14.06,9M17.66,3C17.41,3 17.15,3.1 16.96,3.29L15.13,5.12L18.88,8.87L20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18.17,3.09 17.92,3 17.66,3M14.06,6.19L3,17.25V21H6.75L17.81,9.94L14.06,6.19Z"
                                      />
                                    </svg>
                                    <Form.Control
                                      className="file-upload"
                                      id="profilePicture"
                                      type="file"
                                      accept=".png, .jpg, .jpeg"
                                      name="profilePicture"
                                      onChange={(e) =>
                                        setFieldValue(
                                          "profilePicture",
                                          e.currentTarget.files[0],
                                        )
                                      }
                                    />
                                  </div>
                                </div>
                                <div className="img-extension mt-3">
                                  <div className="d-inline-block align-items-center">
                                    <span>Only</span> <Link to="#">.jpg</Link>{" "}
                                    <Link to="#">.png</Link>{" "}
                                    <Link to="#">.jpeg</Link>{" "}
                                    <span>allowed</span>
                                  </div>
                                </div>
                                <ErrorMessage
                                  name="profilePicture"
                                  component="div"
                                  className="text-danger"
                                />
                                {errors?.profilePicture?.map((error, index) => (
                                  <span
                                    key={index}
                                    className="w-100 mt-1 small text-danger"
                                  >
                                    {error}
                                    <br />
                                  </span>
                                ))}
                              </Form.Group>
                            </>
                          )}
                        </Card.Body>
                      </Card>
                    </div>
                  </Col>

                  <Col xl="9" lg="8" ref={alertRef} className="d-flex">
                    <div style={{ width: "100%", display: "grid" }}>
                      <Card>
                        <Card.Header className="d-flex justify-content-between">
                          <div
                            className="breadcrumb header-title"
                            style={{ cursor: "pointer" }}
                          >
                            <h4
                              onClick={() =>
                                navigate("/dashboard/app/warehouse/user-list")
                              }
                              className="text-primary card-title"
                              style={{ cursor: "pointer" }}
                            >
                              <span>User</span>
                            </h4>
                            <h4 className="mx-2 card-title">{">"}</h4>
                            <h4 className="card-title">User Information</h4>
                          </div>
                        </Card.Header>

                        <Card.Body>
                          {alert.alertFor === "user-edit" && (
                            <AlertNotificationSimple
                              type={alert.type}
                              message={alert.message}
                              show={alert.show}
                              onClose={() =>
                                setAlert({ ...alert, show: false })
                              }
                            />
                          )}
                          {user && !loader && (
                            <div className="new-user-info">
                              <div className="row">
                                <InputField
                                  className="col-md-6 form-group"
                                  type="text"
                                  name="name"
                                  placeholder="Enter Name"
                                  value={values.name}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  asterick={true}
                                  errorMessage={errors?.name}
                                  label="Name"
                                />

                                <InputField
                                  className="col-md-6 form-group"
                                  type="email"
                                  name="email"
                                  placeholder="Email"
                                  value={values.email}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  asterick={true}
                                  disabled={true}
                                  errorMessage={errors?.email}
                                  label="Email"
                                />

                                <Row>
                                  <Form.Group className="col-md-6 form-group">
                                    <Form.Label htmlFor="phoneNumber">
                                      Phone Number
                                    </Form.Label>
                                    <CustomPhoneInput
                                      id="phoneNumber"
                                      name="phoneNumber"
                                      value={values.phoneNumber}
                                      onChange={(value) =>
                                        setFieldValue("phoneNumber", value)
                                      }
                                      onBlur={handleBlur}
                                      placeholder="Enter phone number"
                                      country="us"
                                      isValid={(value, country) => {
                                        if (value.match(/12345/)) {
                                          return `Invalid value: ${value}, ${country.name}`;
                                        } else if (value.match(/1234/)) {
                                          return false;
                                        } else {
                                          return true;
                                        }
                                      }}
                                    />
                                    <ErrorMessage
                                      name="phoneNumber"
                                      component="div"
                                      className="text-danger"
                                    />

                                    {errors?.phoneNumber?.map(
                                      (error, index) => (
                                        <span
                                          key={index}
                                          className="w-100 mt-1 small text-danger"
                                        >
                                          {error}
                                          <br />
                                        </span>
                                      ),
                                    )}
                                  </Form.Group>
                                </Row>
                              </div>
                            </div>
                          )}
                          {loader && <FadeLoaderComponent />}
                        </Card.Body>
                      </Card>
                    </div>
                  </Col>
                </Row>

                {userWarehousesRolesPermissions && !loader && (
                  <Row>
                    <Col xl="12" lg="12" className="d-flex">
                      <div style={{ width: "100%", display: "grid" }}>
                        <Card>
                          <Card.Header className="d-flex justify-content-between pb-3">
                            <div className="header-title">
                              <h4 className="card-title">
                                {decodedToken?.warehouse}
                              </h4>
                            </div>
                          </Card.Header>
                          <Card.Body>
                            {user && !loader && (
                              <div
                                className="user-permission"
                                ref={alertPermissionRef}
                              >
                                <div className="row">
                                  {alert.alertFor ===
                                    "user-permission-edit" && (
                                    <AlertNotificationSimple
                                      type={alert.type}
                                      message={alert.message}
                                      show={alert.show}
                                      onClose={() =>
                                        setAlert({
                                          ...alert,
                                          show: false,
                                        })
                                      }
                                    />
                                  )}
                                  <Row className="mb-3">
                                    <Col>
                                      <h5 className=" align-items-center mb-4">
                                        Roles{" "}
                                      </h5>
                                      <div className="d-flex flex-wrap gap-4">
                                        {options.length > 0 &&
                                          options?.map((role) => {
                                            const isChecked =
                                              values?.roles?.includes(
                                                role?.value,
                                              );

                                            return (
                                              <div
                                                key={role?.value}
                                                className="role-checkbox-item"
                                              >
                                                <Form.Check
                                                  type="checkbox"
                                                  name="roles"
                                                  id={`role-${role?.value}`}
                                                  label={role?.label}
                                                  checked={isChecked}
                                                  disabled={
                                                    !userWarehousesRolesPermissions?.warehouseId
                                                  }
                                                  onChange={(value) => {
                                                    const isRoleBeingRemoved =
                                                      isChecked;
                                                    let updatedRoles =
                                                      isRoleBeingRemoved
                                                        ? values.roles.filter(
                                                            (r) =>
                                                              r !== role?.value,
                                                          )
                                                        : [
                                                            ...values.roles,
                                                            role?.value,
                                                          ];
                                                    setFieldValue(
                                                      "roles",
                                                      updatedRoles,
                                                    );

                                                    let mergedPermissions = {
                                                      ...values.permissions,
                                                    };

                                                    updatedRoles.forEach(
                                                      (roleValue) => {
                                                        const roleOption =
                                                          options.find(
                                                            (option) =>
                                                              option.value ===
                                                              roleValue,
                                                          );

                                                        if (
                                                          roleOption &&
                                                          (roleOption.roleType ===
                                                            roleTypes.ADMIN ||
                                                            roleOption.roleType ===
                                                              roleTypes.SUPER_ADMIN)
                                                        ) {
                                                          if (
                                                            mergedPermissions[
                                                              "warehouse"
                                                            ]
                                                          ) {
                                                            mergedPermissions[
                                                              "warehouse"
                                                            ]["create"] = true;
                                                            mergedPermissions[
                                                              "warehouse"
                                                            ]["delete"] = true;
                                                          }
                                                        }
                                                      },
                                                    );

                                                    setFieldValue(
                                                      "permissions",
                                                      mergedPermissions,
                                                    );
                                                  }}
                                                  onBlur={handleBlur}
                                                  className="mb-2"
                                                />
                                              </div>
                                            );
                                          })}
                                      </div>
                                      <ErrorMessage
                                        name={`roles`}
                                        component="div"
                                        className="text-danger small"
                                      />

                                      {errors?.roles?.map((error, index) => (
                                        <span
                                          key={index}
                                          className="w-100 mt-1 small text-danger"
                                        >
                                          {error}
                                          <br />
                                        </span>
                                      ))}
                                    </Col>
                                  </Row>
                                  <hr className="mt-4 mb-4" />

                                  <Row>
                                    <h5 className=" align-items-center mt-1 mb-4">
                                      Permissions{" "}
                                    </h5>

                                    {errorsformik.permissions &&
                                      touched.permissions &&
                                      typeof errorsformik.permissions ===
                                        "string" && (
                                        <div className="text-danger small mb-3">
                                          {errorsformik.permissions}
                                        </div>
                                      )}

                                    {permissions
                                      .filter(
                                        (permission) =>
                                          permission.slug !== "user",
                                      )
                                      .map((permission) => (
                                        <div
                                          key={permission.id}
                                          className="mb-4 mt-2"
                                        >
                                          <Form.Label
                                            htmlFor="permissions"
                                            className="text-black"
                                          >
                                            {permission.label}
                                          </Form.Label>
                                          <div className="d-flex gap-4 flex-wrap mt-2">
                                            {permission.permissions
                                              .filter((perm) => {
                                                const normalizedEntity = String(
                                                  permission.slug || "",
                                                )
                                                  .toLowerCase()
                                                  .trim();
                                                const normalizedAction = String(
                                                  perm.permission_slug || "",
                                                )
                                                  .toLowerCase()
                                                  .trim();

                                                if (
                                                  normalizedEntity ===
                                                    "warehouse" &&
                                                  (normalizedAction ===
                                                    "create" ||
                                                    normalizedAction ===
                                                      "delete")
                                                ) {
                                                  const selectedRoleIds = (
                                                    values.roles || []
                                                  ).map((r) =>
                                                    r && typeof r === "object"
                                                      ? r.value
                                                      : r,
                                                  );
                                                  const hasAdminRole =
                                                    options.some(
                                                      (opt) =>
                                                        selectedRoleIds.some(
                                                          (id) =>
                                                            String(id) ===
                                                            String(opt.value),
                                                        ) &&
                                                        (opt.roleType ===
                                                          roleTypes.ADMIN ||
                                                          opt.roleType ===
                                                            roleTypes.SUPER_ADMIN),
                                                    );
                                                  return !!hasAdminRole;
                                                }
                                                if (perm.display !== true)
                                                  return false;
                                                return true;
                                              })
                                              .map((perm) => (
                                                <label
                                                  key={perm.permission_slug}
                                                  className="d-flex align-items-center"
                                                >
                                                  <Field
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    name={`permissions.${permission.slug}.${perm.permission_slug}`}
                                                    checked={
                                                      values?.permissions?.[
                                                        permission.slug
                                                      ]?.[
                                                        perm.permission_slug
                                                      ] || false
                                                    }
                                                    onChange={(e) => {
                                                      const handlePermissionChange =
                                                        (
                                                          e,
                                                          permission,
                                                          perm,
                                                          values,
                                                          setFieldValue,
                                                        ) => {
                                                          const isChecked =
                                                            e.target.checked;
                                                          const entityKey =
                                                            permission.slug;
                                                          const actionKey =
                                                            perm.permission_slug.toLowerCase();

                                                          const currentPermissions =
                                                            values
                                                              .permissions?.[
                                                              entityKey
                                                            ] || {};

                                                          const updatedEntityPermissions =
                                                            {
                                                              ...currentPermissions,
                                                              [actionKey]:
                                                                isChecked,
                                                            };

                                                          const requiresViewActions =
                                                            [
                                                              "update",
                                                              "delete",
                                                              "status",
                                                            ];
                                                          const isViewAction =
                                                            actionKey ===
                                                            "view";

                                                          if (!isViewAction) {
                                                            if (
                                                              isChecked &&
                                                              requiresViewActions.includes(
                                                                actionKey,
                                                              )
                                                            ) {
                                                              updatedEntityPermissions[
                                                                "view"
                                                              ] = true;
                                                            } else if (
                                                              !isChecked &&
                                                              requiresViewActions.includes(
                                                                actionKey,
                                                              )
                                                            ) {
                                                              const otherChecked =
                                                                requiresViewActions.some(
                                                                  (act) =>
                                                                    act !==
                                                                      actionKey &&
                                                                    currentPermissions?.[
                                                                      act
                                                                    ],
                                                                );

                                                              if (
                                                                !otherChecked
                                                              ) {
                                                                const manuallyEnabledView =
                                                                  currentPermissions?.[
                                                                    "view"
                                                                  ];
                                                                if (
                                                                  !manuallyEnabledView
                                                                ) {
                                                                  updatedEntityPermissions[
                                                                    "view"
                                                                  ] = false;
                                                                }
                                                              }
                                                            }
                                                          } else {
                                                            if (!isChecked) {
                                                              const anyRequiredChecked =
                                                                requiresViewActions.some(
                                                                  (act) =>
                                                                    currentPermissions?.[
                                                                      act
                                                                    ],
                                                                );

                                                              if (
                                                                anyRequiredChecked
                                                              ) {
                                                                return;
                                                              }
                                                            }
                                                          }

                                                          setFieldValue(
                                                            `permissions.${entityKey}`,
                                                            updatedEntityPermissions,
                                                          );
                                                        };

                                                      handlePermissionChange(
                                                        e,
                                                        permission,
                                                        perm,
                                                        values,
                                                        setFieldValue,
                                                      );
                                                    }}
                                                  />
                                                  <span className="ms-2">
                                                    {perm.label}
                                                  </span>
                                                </label>
                                              ))}
                                          </div>
                                        </div>
                                      ))}
                                    {errors?.permissions?.map(
                                      (error, index) => (
                                        <span
                                          key={index}
                                          className="w-100 mt-1 small text-danger"
                                        >
                                          {error}
                                          <br />
                                        </span>
                                      ),
                                    )}
                                  </Row>
                                </div>
                                <hr />
                                <Button
                                  type="submit"
                                  disabled={isSubmitting}
                                  variant="btn btn-primary"
                                >
                                  Update
                                </Button>
                              </div>
                            )}
                          </Card.Body>
                        </Card>
                      </div>
                    </Col>
                  </Row>
                )}
              </Form>
            );
          }}
        </Formik>
      </div>
    </>
  );
};

export default WarehouseAccountEdit;
